import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import imageCompression from "browser-image-compression";
import { getSignature } from "./cloudinary";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Uploads an image to Cloudinary.
 *
 * This function handles the process of uploading an image file or blob to Cloudinary.
 * It first fetches a signature from the backend, then uses that signature to upload the
 * image to Cloudinary.
 *
 * @param {File | Blob} image - The image file or blob to be uploaded.
 * @returns {Promise<string>} A promise that resolves to the public ID of the uploaded image.
 * @throws {Error} Throws an error if something goes wrong during the upload process.
 */
type UploadOptions = {
  onProgress?: (progress: number) => void;
  onStart?: () => void;
  onComplete?: () => void;
};

function blobToFile(blob: Blob, fileName: string, mimeType?: string): File {
  return new File([blob], fileName, {
    type: mimeType || blob.type,
    lastModified: Date.now(),
  });
}

export async function uploadImageToCloudinary(
  image: File | Blob,
  options: UploadOptions = {}
): Promise<string> {
  const { onProgress, onStart, onComplete } = options;

  try {
    onStart?.();

    const { timestamp, signature } = await getSignature();

    let file =
      image instanceof File
        ? image
        : blobToFile(
            image,
            `image-${Date.now()}.${image.type.split("/")[1] || "webp"}`
          );

    const isImageType = file.type.startsWith("image/");
    if (isImageType) {
      file = await imageCompression(file, {
        maxSizeMB: 2,
        maxWidthOrHeight: 1000,
        useWebWorker: true,
        fileType: "image/webp",
      });
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("timestamp", timestamp.toString());
    formData.append("signature", signature);
    formData.append("api_key", process.env.NEXT_PUBLIC_CLOUDINARY_API_KEY!);

    return await new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100
          );
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText);

          resolve(response.public_id);
        } else {
          reject(new Error(`Upload failed: ${xhr.responseText}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during upload"));
      });

     xhr.open(
        "POST",
        `https://api.cloudinary.com/v1_1/${
          process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME
        }/auto/upload`
      );
      xhr.send(formData);
    });
  } catch (e) {
    console.error(e);
    throw new Error("Something went wrong while uploading");
  } finally {
    onComplete?.();
  }
}

/**
 * Uploads a chat attachment to Cloudinary and returns the full `secure_url`
 * (unlike `uploadImageToCloudinary`, which returns a bare public_id — other
 * callers of that function rely on the bare id for delete-by-public_id, so
 * this is a separate function rather than a change to its return value).
 * Cloudinary's `/auto/upload` endpoint picks `resource_type` (image vs raw)
 * per file, and `secure_url` already contains the correct `/image/upload/`
 * or `/raw/upload/` prefix for that choice.
 */
export async function uploadChatAttachmentToCloudinary(
  file: File | Blob,
  options: UploadOptions = {}
): Promise<string> {
  const { onProgress, onStart, onComplete } = options;

  try {
    onStart?.();

    const { timestamp, signature } = await getSignature();

    let uploadFile =
      file instanceof File
        ? file
        : blobToFile(file, `file-${Date.now()}.${file.type.split("/")[1] || "bin"}`);

    const isImageType = uploadFile.type.startsWith("image/");
    if (isImageType) {
      uploadFile = await imageCompression(uploadFile, {
        maxSizeMB: 2,
        maxWidthOrHeight: 1000,
        useWebWorker: true,
        fileType: "image/webp",
      });
    }

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("timestamp", timestamp.toString());
    formData.append("signature", signature);
    formData.append("api_key", process.env.NEXT_PUBLIC_CLOUDINARY_API_KEY!);

    return await new Promise<string>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100
          );
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText);

          resolve(response.secure_url);
        } else {
          reject(new Error(`Upload failed: ${xhr.responseText}`));
        }
      });

      xhr.addEventListener("error", () => {
        reject(new Error("Network error during upload"));
      });

      xhr.open(
        "POST",
        `https://api.cloudinary.com/v1_1/${
          process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME
        }/auto/upload`
      );
      xhr.send(formData);
    });
  } catch (e) {
    console.error(e);
    throw new Error("Something went wrong while uploading");
  } finally {
    onComplete?.();
  }
}

export const currencies = [
  { value: "NGN", label: "Naira - ₦", symbol: "₦" },
  { value: "USD", label: "Dollar - $", symbol: "$" },
] as const;

export const solutions = [
  {
    id: "1",
    headTitle: "Custom Web & App Development",
    miniSolutions: [
      {
        id: "1",
        subTitle: "Web Development",
        description:
          "Build a responsive, user-friendly, and functional website tailored to meet your specific needs.",
        solutionId: "1",
      },
      {
        id: "2",
        subTitle: "App Development",
        description:
          "Build a mobile applications for iOS and Android for any use case.",
        solutionId: "1",
      },
      {
        id: "3",
        subTitle: "eCommerce",
        description:
          "Build store with secure payments, inventory control and key e-commerce features.",
        solutionId: "1",
      },
      {
        id: "4",
        subTitle: "API Integration",
        description:
          "Building and integrating third-party services and APIs such as AI for comprehensive solutions.",
        solutionId: "1",
      },
      {
        id: "5",
        subTitle: "CMS Development",
        description:
          "Build and manage your business website with custom CMS solutions like WordPress.",
        solutionId: "1",
      },
    ],
  },
  {
    id: "2",
    headTitle: "UI/UX Design & Branding",
    miniSolutions: [
      {
        id: "6",
        subTitle: "UI/UX Design",
        description:
          "Create intuitive and engaging user interfaces that provide exceptional user experiences.",
        solutionId: "2",
      },
      {
        id: "7",
        subTitle: "Brand Identity",
        description:
          "Develop a comprehensive brand identity including logos, color schemes, and brand guidelines.",
        solutionId: "2",
      },
      {
        id: "8",
        subTitle: "Prototyping",
        description:
          "Create interactive prototypes to visualize and test your ideas before development.",
        solutionId: "2",
      },
      {
        id: "9",
        subTitle: "Design Systems",
        description:
          "Build scalable design systems that ensure consistency across all your digital products.",
        solutionId: "2",
      },
    ],
  },
  {
    id: "3",
    headTitle: "Maintenance, Support & Optimization",
    miniSolutions: [
      {
        id: "10",
        subTitle: "Website Maintenance",
        description:
          "Keep your website running smoothly with regular updates, security patches, and performance optimization.",
        solutionId: "3",
      },
      {
        id: "11",
        subTitle: "Technical Support",
        description:
          "Get reliable technical support and troubleshooting for all your digital solutions.",
        solutionId: "3",
      },
      {
        id: "12",
        subTitle: "Performance Optimization",
        description:
          "Optimize your website and applications for better speed, SEO, and user experience.",
        solutionId: "3",
      },
      {
        id: "13",
        subTitle: "Security Audits",
        description:
          "Regular security assessments and implementations to protect your digital assets.",
        solutionId: "3",
      },
    ],
  },
];



export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export function formatPrice(
  minPrice: number,
  currency: string = "USD",
  maxPrice?: number,
  priceType?: "FIXED" | "RANGE"
): string {
  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n)

  
  if (priceType === "RANGE" && maxPrice != null && maxPrice > minPrice) {
    return `${fmt(minPrice)} – ${fmt(maxPrice)}`
  }
  if (minPrice<1) {
    return `Contact us`
  }
  return fmt(minPrice)
}